// src/pages/AIPage.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { BsSend, BsList, BsArrowRight } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import ChatSidebar from '../components/ai/ChatSidebar';
import MessageBubble from '../components/ai/MessageBubble';
import WelcomeScreen from '../components/ai/WelcomeScreen';
import ThinkingSteps from '../components/ai/ThinkingSteps';
import { useSession } from '../hooks/useSession';
import { fetchSessionsWithToken } from '../api/ai';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="text-red-500 text-xs p-2 border border-red-300 rounded-lg bg-red-50">
          خطا در نمایش پیام. لطفاً صفحه را رفرش کنید.
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AIPage() {
  const { isLoggedIn, openAuthModal, getAuthTokens } = useAuthContext();
  const navigate = useNavigate();

  const [chatStarted, setChatStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const tokenRef = useRef(null);

  const getToken = useCallback(() => {
    try {
      let token = null;
      if (getAuthTokens && typeof getAuthTokens === 'function') {
        const tokens = getAuthTokens();
        if (tokens && tokens.access) token = tokens.access;
        else if (tokens && typeof tokens === 'string') token = tokens;
      }
      if (!token) {
        token = localStorage.getItem('access_token') || localStorage.getItem('token');
      }
      if (token) tokenRef.current = token;
      return token || tokenRef.current;
    } catch {
      return tokenRef.current || null;
    }
  }, [getAuthTokens]);

  const {
    sessionId, setSessionId, sessionFull, setSessionFull,
    startNewSession, loadSessionMessages, persistMessage,
  } = useSession(getToken);

  const [containerRect, setContainerRect] = useState(null);
  useEffect(() => {
    const update = () => {
      const mainArea = document.querySelector('.main-content-area');
      if (mainArea) setContainerRect(mainArea.getBoundingClientRect());
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    document.body.classList.add('hide-bottom-nav');
    return () => document.body.classList.remove('hide-bottom-nav');
  }, []);

  useEffect(() => {
    document.body.classList.add('ai-page-active');
    return () => document.body.classList.remove('ai-page-active');
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingSteps]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  }, [input]);

  const safePersistMessage = useCallback(async (role, content) => {
    try { await persistMessage(role, content); } catch { /* ignore */ }
  }, [persistMessage]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const token = getToken();
    if (!token) return;
    (async () => {
      try {
        await fetchSessionsWithToken(token);
      } catch (e) {
        if (e.message === '401') openAuthModal();
      }
    })();
  }, [isLoggedIn, getToken, openAuthModal]);

  const handleSelectSession = async (sid) => {
    setSessionId(sid);
    setChatStarted(true);
    setSessionFull(false);
    const msgs = await loadSessionMessages(sid);
    setMessages(msgs.length ? msgs : []);
  };

  const handleNewChat = () => {
    startNewSession();
    setMessages([]);
    setChatStarted(false);
  };

  const sendMessage = async (userMessage) => {
    try {
      if (!isLoggedIn) { openAuthModal(); return; }
      const token = getToken();
      if (!token) { openAuthModal(); return; }
      if (!userMessage.trim() || sessionFull) return;

      if (!chatStarted) setChatStarted(true);

      const userMsgObj = { id: Date.now(), role: 'user', content: userMessage.trim() };
      setMessages(prev => [...prev, userMsgObj]);
      safePersistMessage('user', userMessage.trim());

      const assistantMsgId = Date.now() + 1;
      const assistantMsg = {
        id: assistantMsgId, role: 'assistant', content: '',
        thinkingSteps: [], cards: null, user_query: userMessage.trim(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setIsLoading(true);
      setInput('');
      setThinkingSteps([]);

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await fetch('https://api.farzaneh.app/api/v1/ai/api/chat/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: userMessage, session_id: sessionId, search_web: false, deep_search: true }),
        signal: abortController.signal,
      });

      if (response.status === 400) {
        const errData = await response.json();
        if (errData.error?.includes('حداکثر ۲۰ پرسش')) {
          setSessionFull(true);
          throw new Error('حداکثر ۲۰ پرسش. لطفاً گفتگوی جدید شروع کنید.');
        }
      }

      if (!response.ok) {
        if (response.status === 401) { openAuthModal(); return; }
        throw new Error(`خطای سرور: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let currentResponseId = null;
      const currentThinking = [];
      let lineBuffer = '';

      const processLine = (line) => {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') return;
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'cards') {
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMsgId ? { ...msg, cards: (parsed.data || []).map(c => ({ ...c, itemType: c.type === 'book' ? 'book' : 'product' })) } : msg
              ));
            } else if (parsed.type === 'meta' && parsed.response_id) {
              currentResponseId = parsed.response_id;
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMsgId ? { ...msg, response_id: currentResponseId } : msg
              ));
            } else if (parsed.type === 'status') {
              currentThinking.push({ content: parsed.content });
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMsgId ? { ...msg, thinkingSteps: [...currentThinking] } : msg
              ));
              setThinkingSteps([...currentThinking]);
            } else if (parsed.text) {
              accumulatedText += parsed.text;
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMsgId ? { ...msg, content: accumulatedText } : msg
              ));
            }
          } catch { /* ignore */ }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        lineBuffer += chunk;
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() || '';
        for (const line of lines) processLine(line);
      }

      if (lineBuffer.trim().length > 0) {
        for (const line of lineBuffer.split('\n')) processLine(line);
      }

      setMessages(prev => prev.map(msg =>
        msg.id === assistantMsgId ? { ...msg, thinkingSteps: currentThinking } : msg
      ));

      if (accumulatedText) safePersistMessage('assistant', accumulatedText);

    } catch (error) {
      if (error.name === 'AbortError') return;
      setMessages(prev => prev.map(msg =>
        msg.id === Date.now() + 1 ? { ...msg, content: `⚠️ ${error.message}` } : msg
      ));
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      setThinkingSteps([]);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input.trim() && !isLoading && !sessionFull) sendMessage(input.trim());
  };

  let portalStyle;
  if (containerRect) {
    portalStyle = {
      position: 'fixed', top: containerRect.top, left: containerRect.left,
      width: containerRect.width, height: containerRect.height, zIndex: 40, overflow: 'hidden',
    };
  } else {
    return <div style={{ width: '100vw', height: '100vh' }} />;
  }

  const headerIconColor = chatStarted ? 'text-gray-700' : 'text-gray-600';

  return ReactDOM.createPortal(
    <>
      <Helmet><title>دستیار هوشمند | فرزانه</title></Helmet>
      <style>{`
        @keyframes neonPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(172, 138, 220, 0.09), 0 0 20px rgba(96, 165, 250, 0.09); }
          50% { box-shadow: 0 0 15px rgba(125, 58, 164, 0.1), 0 0 30px rgba(96, 165, 250, 0.18); }
        }
        .neon-glow { animation: neonPulse 3s ease-in-out infinite; }
      `}</style>

      <ChatSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onSelectSession={handleSelectSession} currentSessionId={sessionId} onNewChat={handleNewChat} />

      <div style={portalStyle} className="bg-gradient-to-t from-purple-100 via-blue-100 to-teal-50 flex flex-col">
        <div className="sticky top-0 z-20 flex items-center justify-between h-14 px-4 bg-white/60 backdrop-blur-md shrink-0">
          <button onClick={() => navigate(-1)} className={`p-2 ${headerIconColor}`}><BsArrowRight size={20} /></button>
          <div className="w-10" />
          <button onClick={() => setSidebarOpen(true)} className={`p-2 ${headerIconColor}`}><BsList size={20} /></button>
        </div>

        <div className="flex-1 flex flex-col min-h-0 bg-white/40 backdrop-blur-xl overflow-hidden relative">
          {!chatStarted ? (
            <WelcomeScreen onSend={sendMessage} />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-2 py-4" style={{ paddingBottom: '80px' }}>
                {sessionFull && (
                  <div className="text-center text-red-600 bg-red-50 p-2 rounded-lg mb-4">
                    حداکثر ۲۰ پرسش. <button onClick={handleNewChat} className="underline">گفتگوی جدید</button>
                  </div>
                )}
                <div className="space-y-4">
                  {messages.map(msg => (
                    <ErrorBoundary key={msg.id}><MessageBubble message={msg} getToken={getToken} /></ErrorBoundary>
                  ))}
                  {isLoading && thinkingSteps.length > 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white/30 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/20 shadow-sm w-fit">
                        <ThinkingSteps steps={thinkingSteps} />
                      </div>
                    </div>
                  )}
                  {isLoading && thinkingSteps.length === 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white/40 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/30 shadow-sm">
                        <span className="text-sm text-gray-600">در حال پردازش...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="absolute bottom-5 left-0 right-0 px-3 pt-3 z-30" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <div className="flex flex-col gap-2">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={textareaRef} value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
                      placeholder="سوال خود را اینجا بنویسید…" rows={1}
                      className="flex-1 min-h-[48px] max-h-[140px] px-4 py-3 bg-white/30 backdrop-blur-md border border-gray-300 rounded-2xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 transition-all text-sm resize-none leading-6 placeholder:text-gray-500 text-gray-800"
                      disabled={isLoading || sessionFull}
                    />
                    <button
                      type="button" onClick={() => sendMessage(input.trim())}
                      disabled={!input.trim() || isLoading || sessionFull}
                      className={`relative z-10 h-[48px] w-[48px] rounded-2xl flex items-center justify-center shrink-0 transition-all duration-200 ${input.trim() && !isLoading && !sessionFull ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 active:scale-95' : 'bg-gray-200/40 text-gray-400 cursor-not-allowed'}`}
                      aria-label="ارسال پیام"
                    ><BsSend size={17} /></button>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">Enter ارسال &nbsp;·&nbsp; Shift+Enter خط جدید</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>, document.body
  );
}